import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ImageCropper from "./ImageCropper"; // Adjust the import path as necessary
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { LoadingButton } from "@mui/lab";
import {
  useAddEvent,
  useFetchBands,
  useFetchEventById,
  useUpdateEvent,
} from "../../../hooks";
import { useFetchEventsByVenueId } from "../../../hooks/eventHooks";
import { useLocation, useNavigate } from "react-router";
import firebase from "firebase/compat/app";
import { useFetchVenueById, useFetchVenuesSummary } from "../../../hooks/venueHooks";
import { generateCleanWordsWithLimit } from "../../../utils/utility";
import PaginatedAutocomplete from "../../Elements/PaginatedAutocomplete";
import { auth, db } from "../../../firebase-config";
import { buildSearchTerms } from "../../../utils/searchTerms";
import { GeoFirestore } from "geofirestore";

const geoFirestore = new GeoFirestore(db);

const EventForm = () => {
  const location = useLocation();
  // Start loading venue & band data immediately so it's ready by the time the
  // user clicks those fields. sessionStorage caching means subsequent page
  // visits are instant (no Firestore read until the 10-min TTL expires).
  const [enableVenueApi, setEnableVenueApi] = useState(true);
  const [enableBandApi, setEnableBandApi] = useState(true);
  const [venueRefresh, setVenueRefresh] = useState(0);
  const [bandRefresh, setBandRefresh] = useState(0);
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get("eventId");

  // const eventData = state?.events.find((e) => e.id == eventId);
  const { event: eventData, loading } = useFetchEventById(eventId);
  // const isEditMode = Boolean(eventData);
  const [isEditMode, setIsEditMode] = useState(false);
  const { venue: eventVenue } = useFetchVenueById(eventData?.venue);
  const { bands: allBands, loading: allBandsLoading } = useFetchBands(
    bandRefresh,
    enableBandApi
  );
  const [sampleVideoUrl, setSampleVideoUrl] = useState("");

  // ⚡ Use lightweight summary (no embeddings) - reduces 12.7MB to ~1MB
  const { venues: allVenues, loading: allVenuesLoading } = useFetchVenuesSummary(
    venueRefresh,
    enableVenueApi
  );

  const [guid, setGuid] = useState(uuidv4());
  const [title, setTitle] = useState("");
  const [venue, setVenue] = useState(null);
  const [date, setDate] = useState(
    dayjs()
      .set("hour", 19)
      .set("minute", 0)
      .set("second", 0)
      .set("millisecond", 0)
  );
  const [bands, setBands] = useState([]);
  const [eventType, setEventType] = useState("Music");
  const [facebookId, setFacebookId] = useState("");
  const [ticketUrl, setTicketUrl] = useState("");
  const [description, setDescription] = useState("");
  const [croppedImage, setCroppedImage] = useState("");
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    title: "",
    venue: "",
    eventType: "",
    date: "",
  });

  const { addEvent, loading: addingEvent } = useAddEvent();
  const { updateEvent, loading: updatingEvent } = useUpdateEvent();
  const navigate = useNavigate();
  const [highlight, setHighlight] = useState(false); // Add state for highlight

  // ── Feature 1: Fetch upcoming events for the selected venue ──────────────
  const {
    events: venueEvents,
    loading: venueEventsLoading,
  } = useFetchEventsByVenueId(venue?.id || null, false);

  // ── Feature 2: Suggest bands whose name/alias appears in the title ────────
  const suggestedBands = useMemo(() => {
    if (!title || title.length < 3 || !allBands?.length) return [];
    const lowerTitle = title.toLowerCase();
    return allBands.filter((band) => {
      const nameMatch = band.bandname && lowerTitle.includes(band.bandname.toLowerCase());
      const aliasMatch = Array.isArray(band.alias) && band.alias.some(
        (a) => a && lowerTitle.includes(a.toLowerCase())
      );
      return nameMatch || aliasMatch;
    });
  }, [title, allBands]);

  const addSuggestedBand = (band) => {
    if (!bands.find((b) => b.id === band.id)) {
      setBands((prev) => [...prev, band]);
    }
  };

  function buildEmbeddingText({
    title,
    bands,
    venue_name,
    city,
    state,
    eventdescription,
  }) {
    const bandNames = Array.isArray(bands)
      ? bands
          .map((b) => b?.bandname)
          .filter(Boolean)
          .join(", ")
      : "";

    const parts = [
      title || "",
      bandNames,
      `${venue_name || ""} ${city || ""} ${state || ""}`.trim(),
      (eventdescription || "").replace(/<[^>]+>/g, " "), // strip HTML if any
    ]
      .map((s) => (s || "").trim())
      .filter(Boolean);

    return parts.join(" | ");
  }

  useEffect(() => {
    if (!sampleVideoUrl && bands?.length > 0) {
      const bandWithVideo = bands.find((b) => b.sampleVideoUrl);
      if (bandWithVideo) {
        setSampleVideoUrl(bandWithVideo.sampleVideoUrl);
      }
    }
  }, [bands, sampleVideoUrl]);

  useEffect(() => {
    if (eventData) {
      setIsEditMode(true);
      setGuid(eventData.id);
      setTitle(eventData.title || "");
      setDate(eventData.main_date ? dayjs(eventData.main_date) : dayjs());
      setBands(eventData.bands || []);
      setEventType(eventData.eventtype || "");
      setFacebookId(eventData.fbid || "");
      setTicketUrl(eventData.ticket_url || "");
      setDescription(eventData.eventdescription || "");
      setHighlight(eventData.highlight || false);
      setSampleVideoUrl(eventData.sampleVideoUrl || "");
      // setImage(eventData.imageUrl || "");
    }
    if (eventVenue) {
      setVenue(eventVenue || null);
    }
  }, [eventData, eventVenue]);

  const validate = () => {
    let temp = {};
    temp.title = title ? "" : "This field is required.";
    temp.venue = venue ? "" : "This field is required.";
    temp.eventType = eventType ? "" : "This field is required.";
    temp.date = date.isValid() ? "" : "Invalid date.";
    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const eventTypes = [
    "Music",
    "Trivia",
    "Open Mic",
    "Karaoke",
    "Comedy",
    "Paint",
    "Games",
    "Fundraiser",
    "Play",
    "DJ",
    "Others",
  ];

  const handleImageCrop = (img, croppedDataUrl) => {
    if (!img || !croppedDataUrl) {
      setImage("");
      return;
    }
    setImage(img);
    setCroppedImage(croppedDataUrl);
  };

  const handleImageUpload = async (imageFile) => {
    if (!imageFile) {
      return isEditMode ? eventData?.imageUrl || "" : "";
    }
    const storage = getStorage();
    const storageRef = ref(storage, "events/" + title.replaceAll(" ", "-"));

    try {
      const snapshot = await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image: ", error);
      return "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validate()) {
      setIsLoading(true);

      // Check for duplicate events (like scrapeNYC.js)
      if (!isEditMode) {
        try {
          const geocollection = geoFirestore.collection("events");
          const docRef = geocollection.doc(guid);
          const snap = await docRef.get();
          
          if (snap.exists) {
            console.warn(`[addEvent] Event with ID ${guid} already exists`);
            setErrors({
              ...errors,
              title: "An event with this ID already exists. Please refresh the page to generate a new ID.",
            });
            setIsLoading(false);
            return;
          }
        } catch (err) {
          console.error("[addEvent] Error checking for duplicate:", err);
          // Continue with submission if check fails
        }
      }

    const imageUrl = await handleImageUpload(image);
    console.log("Selected date and time:", date.format("YYYY-MM-DD HH:mm:ss"));

    const user = auth.currentUser;
    const userId = user ? user.uid : null;

    // 🔹 build embeddingText from the current form values
    const embeddingText = buildEmbeddingText({
      title,
      bands,
      venue_name: venue?.venue_name,
      city: venue?.venue_city,
      state: venue?.venue_state,
      eventdescription: description,
    });

      const formData = {
        id: guid,
        title,
        title_lowercase: title.toLowerCase(),
        venue: venue?.id,
        main_date: firebase.firestore.Timestamp.fromDate(date.toDate()),
        bandIds: bands.map((b) => b.id),
        bands,
        eventtype: eventType,
        fbid: facebookId,
        ticket_url: ticketUrl,
        eventdescription: description,
        modifieddate: firebase.firestore.Timestamp.fromDate( new Date(dayjs().toISOString())),
        imageUrl,
        city: venue?.venue_city,
        venue_name: venue?.venue_name,
        venue_state: venue?.venue_state,
        state: venue?.venue_state,
        address: venue?.address1,
        zip: venue?.VENUE_ZIP,
        venue_internallink: venue?.internallink || "",
        lat: parseFloat(venue?.lat),
        lon: parseFloat(venue?.lon),
        userId: userId,
        highlight,
        sampleVideoUrl,
        embeddingText,
        embeddingModel: "text-embedding-3-small", // or whatever you’ll use
        embeddingNeedsUpdate: true,               // handy flag for your backfill job
        searchTerms: buildSearchTerms({
          title,
          bands,
          venue_name: venue?.venue_name,
          venue_internallink: venue?.internallink || "",
          city: venue?.venue_city,
          state: venue?.venue_state,
        }),
        // embedding: [] // leave for your Cloud Function to fil
      };
      if (isEditMode) {
        updateEvent(formData?.id, formData).then(() => {
          setGuid(uuidv4());
          setIsLoading(false);
        });
      } else {
        addEvent(formData).then(() => {
          setGuid(uuidv4());
          setIsLoading(false);
        });
      }
    }
    // Handle the submission here, such as sending data to Firebase
  };
  const navigateToDetails = (id) => {
    if (id) {
      navigate(`/events?eventId=${id}`, {
        state: { fromNavigateToDetails: true },
      });
    } else {
      navigate("/events");
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
      </Box>
    );
  }
  return (
    <Container maxWidth={false} sx={{ mt: 12 }}>
      <Card
        raised
        sx={{ margin: "auto", maxWidth: "calc(100% - 32px)", boxShadow: 3 }}
      >
        <Box sx={{ bgcolor: "#307fc1", color: "white", p: 2 }}>
          <Typography variant="h6">Add Event</Typography>
        </Box>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <ImageCropper
                src={isEditMode ? eventData?.imageUrl : ""}
                onCrop={handleImageCrop}
              />

              {/* <Typography variant="subtitle1" sx={{ mt: 6, color: "gray" }}>
                ID: {guid}
              </Typography> */}
              <br></br>
              <br></br>
              <Grid container spacing={3} sx={{ my: 2 }}>
                {/* Other input fields */}
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={highlight}
                        onChange={(e) => setHighlight(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Highlight this event"
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Title of the Event"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={!!errors.title}
                helperText={errors.title || ""}
                sx={{ my: 2 }}
                required
              />

              {/* ── Feature 2: Band suggestions from title ── */}
              <Collapse in={title.length >= 3 && allBands?.length > 0}>
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "info.light",
                    borderRadius: 1,
                    background: "#e3f2fd",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    🎵 Band matches from title ({allBands?.length || 0} bands loaded):
                  </Typography>
                  {suggestedBands.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      No band names found in title yet. Try including a band name in the title above.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {suggestedBands.map((band) => {
                        const alreadyAdded = bands.some((b) => b.id === band.id);
                        return (
                          <Chip
                            key={band.id}
                            label={band.bandname}
                            color={alreadyAdded ? "success" : "primary"}
                            variant={alreadyAdded ? "filled" : "outlined"}
                            disabled={alreadyAdded}
                            onClick={() => addSuggestedBand(band)}
                            size="small"
                            clickable={!alreadyAdded}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Collapse>

              <Grid container spacing={3} alignItems="flex-start">
                {/* Left column: venue + date pickers */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <PaginatedAutocomplete
                          options={allVenues || []}
                          getOptionLabel={(option) =>
                            `${option.venue_name}, (${option.venue_city})`
                          }
                          value={venue}
                          onChange={(event, newValue) => {
                            setVenue(newValue);
                          }}
                          error={!!errors.venue}
                          helperText={errors.venue}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          label={"Select Venue"}
                          onFocus={() => setEnableVenueApi(true)}
                          isLoading={allVenuesLoading}
                          sx={{ flex: 1 }}
                        />
                        <Tooltip title="Refresh venue list">
                          <IconButton
                            onClick={() => {
                              setEnableVenueApi(true);
                              setVenueRefresh((n) => n + 1);
                            }}
                            sx={{ mt: 1 }}
                            color="primary"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DateTimePicker
                        sx={{ width: "100%" }}
                        label="Date & Time"
                        value={date}
                        onChange={setDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.date}
                            helperText={errors.date || ""}
                            fullWidth
                          />
                        )}
                        inputFormat="yyyy-MM-dd HH:mm:ss"
                        required
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* ── Feature 1: Existing events at selected venue (right column) ── */}
                <Grid item xs={12} md={4}>
                  <Collapse in={!!venue}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "warning.light",
                        borderRadius: 1,
                        background: "#fff8e1",
                        maxHeight: 220,
                        overflowY: "auto",
                      }}
                    >
                      {(() => {
                        const selectedDateStr = date?.isValid() ? date.format("YYYY-MM-DD") : null;
                        const filteredByDate = venueEvents.filter((e) => {
                          if (isEditMode && e.id === eventId) return false;
                          if (!selectedDateStr) return true;
                          return e.main_date?.startsWith(selectedDateStr);
                        });
                        const hasDateFilter = selectedDateStr !== null;
                        return (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                              📅 Events at {venue?.venue_name}{hasDateFilter ? ` on ${selectedDateStr}` : ""}:
                            </Typography>
                            {venueEventsLoading ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption" color="text.secondary">Loading…</Typography>
                              </Box>
                            ) : filteredByDate.length === 0 ? (
                              <Typography variant="caption" color="success.main">
                                ✓ No events on this date.
                              </Typography>
                            ) : (
                              <List dense disablePadding>
                                {filteredByDate.map((e) => (
                                  <ListItem key={e.id} disablePadding sx={{ py: 0.25 }}>
                                    <ListItemText
                                      primary={
                                        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", lineHeight: 1.3 }}>
                                          {e.title}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                          {e.main_date}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Collapse>
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, my: 2 }}>
                <PaginatedAutocomplete
                  multiple
                  options={allBands || []}
                  getOptionLabel={(option) =>
                    option?.alias?.length > 0
                      ? `${option.bandname} aka: ${option.alias.join(", ")}`
                      : option.bandname
                  }
                  required={false}
                  onChange={(event, newValue) => setBands(newValue)}
                  value={bands}
                  label={"Select Band(s)"}
                  onFocus={() => setEnableBandApi(true)}
                  isLoading={allBandsLoading}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.id}
                        label={option.bandname}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Refresh band list">
                  <IconButton
                    onClick={() => {
                      setEnableBandApi(true);
                      setBandRefresh((n) => n + 1);
                    }}
                    sx={{ mt: 1 }}
                    color="primary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Grid container spacing={3} sx={{ my: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth error={!!errors.eventType}>
                    <InputLabel id="event-type-label">Event Type</InputLabel>
                    <Select
                      labelId="event-type-label"
                      id="event-type"
                      value={eventType}
                      label="Event Type"
                      onChange={(e) => setEventType(e.target.value)}
                      required
                    >
                      {eventTypes.map((type, index) => (
                        <MenuItem key={index + type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.eventType || ""}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Facebook ID Numbers only"
                    value={facebookId}
                    onChange={(e) => setFacebookId(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Ticket URL include https://"
                    value={ticketUrl}
                    onChange={(e) => setTicketUrl(e.target.value)}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Event Specific YouTube URL"
                value={sampleVideoUrl}
                onChange={(e) => setSampleVideoUrl(e.target.value)}
                fullWidth
                margin="normal"
              />
              <Grid item xs={12} sm={12} sx={{ pb: 1 }}>
                <Typography variant="body1" sx={{ pt: 1 }}>
                Facebook ID only use the event number:
                <br />
                <a
      href={`https://www.facebook.com/events/${eventData?.fbid || "543736524712699"}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#1976d2", textDecoration: "underline" }}
    >
      https://www.facebook.com/events/<strong style={{ color: "red" }}>{eventData?.fbid || "543736524712699"}</strong>
    </a>
                </Typography>
              </Grid>

              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                sx={{ my: 2 }}
              />

              <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={(e) => {
                    navigateToDetails(null);
                  }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={addingEvent || updatingEvent || isLoading}
                >
                  Submit
                </LoadingButton>
              </Box>
            </form>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EventForm;
